import type { ReceiptAvailability, ReceiptStatus } from './accountTypes.js';
import { receiptsLog } from './logging.js';
import { loadRuntimeConfig } from '../../config/runtimeConfig.js';
import { resolveCustomerCredentialSend } from '../../shared/session/resolveCustomerCredentialSend.js';
import { endCustomerSessionGlobal } from '../../shared/session/customerAuthSync.js';
import { handleUnauthorizedForPath } from '../../shared/session/sessionRefresh.js';
import { stormAwareFetch } from '../../shared/http/stormAwareFetch.js';

export interface ReceiptRowLike {
  transactionId: number;
  receiptStatus: ReceiptStatus;
  receiptAvailability: ReceiptAvailability;
  documentNumber: string | null;
  downloadPath?: string | null;
  receiptKind?:
    | 'PRODUCT_RECEIPT'
    | 'DONATION_CONFIRMATION'
    | 'TAX_INVOICE'
    | 'SIMPLIFIED_TAX_DOCUMENT'
    | 'CORRECTIVE_TAX_DOCUMENT'
    | 'REFUND_CONFIRMATION';
  /** When set, PDF is fetched under this org slug (cross-tenant Účtenky). */
  tenantCode?: string;
}

export function canDownloadInternalReceipt(row: ReceiptRowLike): boolean {
  const path = row.downloadPath?.trim() ?? '';
  return (
    row.receiptStatus === 'ready' &&
    row.receiptAvailability === 'INTERNAL_PDF' &&
    path.length > 0
  );
}

export function receiptDownloadFilename(row: ReceiptRowLike): string {
  const base = row.documentNumber ?? `tx-${row.transactionId}`;
  return `receipt-${base}.pdf`;
}

export function parseFilenameFromContentDisposition(
  header: string | null
): string | null {
  if (header === null || header.length === 0) {
    return null;
  }
  const rfc5987 = /filename\*=(?:UTF-8''|utf-8'')([^;]+)/i.exec(header);
  if (rfc5987?.[1]) {
    try {
      return decodeURIComponent(rfc5987[1].trim());
    } catch (error) {
      receiptsLog.warn('Receipt Content-Disposition filename decode failed', {
        operation: 'parseFilenameFromContentDisposition',
        reason: error instanceof Error ? error.name : 'unknown',
      });
      return rfc5987[1].trim();
    }
  }
  const plain = /filename="([^"]+)"/i.exec(header) ?? /filename=([^;]+)/i.exec(header);
  return plain?.[1]?.trim() ?? null;
}

export interface DownloadAccountReceiptPdfInput {
  apiBaseUrl: string;
  tenantCode: string;
  /**
   * Uses URL-tenant routing; server-side engagement resolves tenant membership on touch.
   * Empty → cookie-first credentials include (XOR); never dual-send with Bearer.
   * Ignored when `platformAbsolute` (parity with `platformAccountFetch` — residual memory Bearer must not drop cookies).
   */
  accessToken?: string | null;
  transactionId: number;
  downloadPath?: string | null;
  /**
   * Wave E — absolute `/api/v1/customer-account/.../receipt.pdf` (platform ownership).
   * When true, `tenantCode` is unused for URL construction and `accessToken` is forced null (cookie-first).
   */
  platformAbsolute?: boolean;
}

/** HTTP failure from receipt.pdf — status is for logs/analytics only, not end-user copy. */
export class ReceiptDownloadHttpError extends Error {
  readonly statusCode: number;

  constructor(statusCode: number) {
    super('receipt_pdf_download_http_error');
    this.name = 'ReceiptDownloadHttpError';
    this.statusCode = statusCode;
  }
}

/** Response looked like success but body is not a PDF (proxy HTML, JSON, empty). */
export class ReceiptDownloadInvalidPdfError extends Error {
  constructor() {
    super('receipt_pdf_download_invalid_pdf');
    this.name = 'ReceiptDownloadInvalidPdfError';
  }
}

export type ReceiptDownloadUserMessageKey =
  | 'account.receipt.downloadFailed'
  | 'account.receipt.downloadSessionExpired'
  | 'account.receipt.downloadNotReady';

/** Maps download failures to localized copy — never surfaces HTTP codes to shoppers. */
export function resolveReceiptDownloadUserMessageKey(
  error: unknown,
): ReceiptDownloadUserMessageKey {
  if (error instanceof ReceiptDownloadHttpError) {
    if (error.statusCode === 401 || error.statusCode === 403) {
      return 'account.receipt.downloadSessionExpired';
    }
    // 404 = not ready / missing object; 422 = platform session/tenant gate or validation
    if (error.statusCode === 404 || error.statusCode === 422) {
      return 'account.receipt.downloadNotReady';
    }
  }
  return 'account.receipt.downloadFailed';
}

/** True when the first bytes are the PDF magic header `%PDF`. */
export function isPdfMagicBytes(bytes: ArrayBuffer | Uint8Array): boolean {
  const view = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  if (view.length < 5) {
    return false;
  }
  return (
    view[0] === 0x25 &&
    view[1] === 0x50 &&
    view[2] === 0x44 &&
    view[3] === 0x46 &&
    view[4] === 0x2d
  );
}

/**
 * G1 — PDF 401/403 auth failure: hard-end customer session the same way platform
 * `/me` / `platformAccountFetch` end on revoked access (clear memory + broadcast).
 * Shared hook for the button and W2 durable remount-hide coordination — do not
 * invent a second local-only hide path as the sole recovery.
 */
export async function onDownloadAuthFailure(): Promise<void> {
  await endCustomerSessionGlobal({
    broadcast: true,
    callApi: false,
    reason: 'revoked',
  });
}

function buildReceiptPdfRequest(input: DownloadAccountReceiptPdfInput): {
  url: string;
  pathForUnauthorized: string;
  headers: Record<string, string>;
  credentials: RequestCredentials;
} {
  const trimmedBase = input.apiBaseUrl.replace(/\/+$/, '');
  const path =
    input.downloadPath?.trim() ||
    `/customer-account/payments/${input.transactionId}/receipt.pdf`;
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const platformAbsolute = input.platformAbsolute === true;
  const url = platformAbsolute
    ? `${trimmedBase}/api/v1${normalizedPath}`
    : `${trimmedBase}/api/${encodeURIComponent(input.tenantCode)}/v1${normalizedPath}`;
  // Spec A: platform absolute always cookie-first — ignore residual memory Bearer
  // (parity with platformAccountFetch / AppDataProvider shouldAttachCustomerBearer: false).
  const credentialSend = resolveCustomerCredentialSend({
    bearerToken: platformAbsolute ? null : input.accessToken,
  });
  const headers: Record<string, string> = {
    Accept: 'application/pdf',
  };
  if (credentialSend.authorizationHeader !== undefined) {
    headers.Authorization = credentialSend.authorizationHeader;
  }
  return {
    url,
    pathForUnauthorized: normalizedPath,
    headers,
    credentials: credentialSend.credentials,
  };
}

async function fetchReceiptPdfOnce(
  url: string,
  headers: Record<string, string>,
  credentials: RequestCredentials,
): Promise<Response> {
  return stormAwareFetch(url, {
    method: 'GET',
    headers,
    credentials,
  });
}

async function parseReceiptPdfResponse(
  response: Response,
): Promise<{ blob: Blob; filename: string | null }> {
  const filename = parseFilenameFromContentDisposition(
    response.headers.get('Content-Disposition')
  );
  const contentType = response.headers.get('Content-Type') ?? '';
  const buffer = await response.arrayBuffer();
  const contentTypeLower = contentType.toLowerCase();
  const contentTypeOk =
    contentType.length === 0 ||
    contentTypeLower.includes('pdf') ||
    contentTypeLower.includes('octet-stream');
  if (!isPdfMagicBytes(buffer) || !contentTypeOk) {
    throw new ReceiptDownloadInvalidPdfError();
  }
  const blob = new Blob([buffer], { type: 'application/pdf' });
  return { blob, filename };
}

/**
 * Authenticated receipt PDF fetch.
 * Callers use URL-tenant routes directly; no client-side tenant-switch preflight.
 * G1 — on 401, same dying-access recovery as {@link fetchPlatformAccountJson}
 * (`handleUnauthorizedForPath` → retry once / end); 401/403 always invalidate
 * session via {@link onDownloadAuthFailure} when the download cannot succeed.
 */
export async function downloadAccountReceiptPdf(
  input: DownloadAccountReceiptPdfInput
): Promise<{ blob: Blob; filename: string | null }> {
  const { url, pathForUnauthorized, headers, credentials } =
    buildReceiptPdfRequest(input);

  let response = await fetchReceiptPdfOnce(url, headers, credentials);

  if (response.status === 401) {
    const action = await handleUnauthorizedForPath(
      loadRuntimeConfig(),
      pathForUnauthorized,
    );
    if (action === 'retry') {
      response = await fetchReceiptPdfOnce(url, headers, credentials);
      if (response.ok) {
        return parseReceiptPdfResponse(response);
      }
    }
    // end | none | still unauthorized after retry — clear phantom signed-in chrome
    // (parity with /me hard-end; Spec A cookie-first jar already empty).
    if (response.status === 401 || response.status === 403 || action === 'end') {
      await onDownloadAuthFailure();
    }
    throw new ReceiptDownloadHttpError(response.status);
  }

  if (response.status === 403) {
    await onDownloadAuthFailure();
    throw new ReceiptDownloadHttpError(403);
  }

  if (!response.ok) {
    throw new ReceiptDownloadHttpError(response.status);
  }

  return parseReceiptPdfResponse(response);
}

export function triggerBlobDownload(blob: Blob, filename: string): void {
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(objectUrl);
}
